import React, { useState, useEffect, useContext, useCallback, memo, FC } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import queryString from "query-string";
import { Button, TextField, SelectPagination, Text } from "@foris/avocado-suite";
import { populationsQuery } from "../graphql/populations.query";
import TermSelect from "@modules/vacancies/Groups/term-select";
import CampusSelect from "@modules/shared/selectors/Selectors/Campus";
import ModalitySelect from "@modules/shared/selectors/Selectors/Modality";
import ProgramSelect from "@modules/shared/selectors/Selectors/Program";
import { PathParams } from "@edhtypes/general";
import { Population } from "@models/ISchema";
import { IOption } from "@common/components/Select/Select";
import { DataReducerType } from "../context/types";
import { Context } from "../context/PackagesContext";
import { Events, useListenEvent } from "@common/hooks/useListenEvent";
import { Types as DataTypes } from "../context/data.reducer";
import css from "./packages.module.scss";

interface ISelectedPopulation {
  index: number;
  value: string;
  level: number;
  self: Population;
}

interface CreationFormProps {
  isLoading?: boolean;
  className?: string;
  showActionButtons?: boolean;
  onValidForm?: (isValid: boolean) => void;
  onSubmit: (dryRun: boolean, skipValidations: boolean, data?: DataReducerType) => void;
  onCancel?: () => void;
}

const CreationForm: FC<CreationFormProps> = ({
  isLoading = false,
  className,
  showActionButtons = true,
  onValidForm,
  onSubmit,
  onCancel,
}) => {
  // Hooks for handle the context and the client
  const client = useApolloClient();
  const { state, dispatch } = useContext(Context);
  const { origin, scenario }: PathParams = useParams();
  const params: queryString.ParsedQuery<string> = queryString?.parse(location?.search);
  const { t } = useTranslation();
  // States for handle the form
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [selectedPopulation, setSelectedPopulation] = useState<ISelectedPopulation>(null);
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedCampus, setSelectedCampus] = useState("");
  const [selectedModality, setSelectedModality] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [packageName, setPackageName] = useState("");
  const [isMounted, setIsMounted] = useState(false);

  const selectorLabel = (population: Population) => {
    if (!population) return "";

    const strongWrapper = (value: string | number) => <strong>{value}</strong>;

    const campus = strongWrapper(population?.campus?.code);
    const program = strongWrapper(population?.program?.code);
    const curriculum = strongWrapper(population?.curriculum);
    const level = strongWrapper(population?.level);
    const modality = strongWrapper(population?.modality?.code);
    const department = strongWrapper(population?.program?.department?.code);
    const shift = strongWrapper(population?.shift?.code);

    return (
      <div className={css.select}>
        <Text type="sm" className={css.select_topLabel}>
          {t("selectors.population-selector.campus")}: {campus} |{" "}
          {t("selectors.population-selector.program")}: {program} |{" "}
          {t("selectors.population-selector.curriculum")}: {curriculum} |{" "}
          {t("selectors.population-selector.level")}: {level}
        </Text>
        <Text type="xs" className={css.select_bottomLabel}>
          {t("selectors.population-selector.school")}: {department} |{" "}
          {t("selectors.population-selector.modality")}: {modality} |{" "}
          {t("selectors.population-selector.shift")}: {shift}
        </Text>
      </div>
    );
  };

  const handlePackageCreation = () => {
    dispatch({
      type: DataTypes.AddCreation,
      payload: {
        population: selectedPopulation?.self,
        termId: selectedTerm,
        linkId: state?.data?.link?.id || undefined,
        packageName,
      },
    });
  };

  /**
   * Reject the populations that have already been created
   *
   * @param populations - Population[]
   * @return Population[]
   */
  const selectablePopulations = (populations: Population[]): Population[] => {
    const createdPopulationCodes = Object.values(state?.data?.creations || {}).reduce(
      (acc: { [key: string]: boolean }, creation) => {
        if (creation?.population?.code) {
          acc[creation.population.code] = true;
        }
        return acc;
      },
      {},
    );

    return populations.filter(population => !createdPopulationCodes[population.code]);
  };

  /**
   * If a creation was added, submit a `dryRun` `packageCrud` request.
   */
  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);
    }

    if (Object.keys(state?.data?.creations ?? {}).length > 0 && isMounted) {
      onSubmit(true, true, state?.data);
    }
  }, [state?.data?.creations]);

  const requestItems = useCallback(
    async (searchTerm = "", page = 1) => {
      const size = 20;

      try {
        const queryParams = {
          query: populationsQuery,
          variables: {
            termId: selectedTerm,
            scenarioId: scenario,
            originId: origin,
            filterId: params?.advance,
            filter: {
              fields: {
                campusId: selectedCampus
                  ? {
                      eq: selectedCampus,
                    }
                  : undefined,
                modalityId: selectedModality
                  ? {
                      eq: selectedModality,
                    }
                  : undefined,
                programId: selectedProgram
                  ? {
                      eq: selectedProgram,
                    }
                  : undefined,
              },
              pagination: { page, size, searchTerm },
            },
          },
        };

        const { data } = await client.query(queryParams);
        const { pageInfo, items } = data?.cube?.complementedPopulations ?? {};

        const populationsToMap = selectablePopulations(items ?? []);
        const options = populationsToMap.map((item: Population) => ({
          label: item?.code,
          value: item.id,
          index: item.packageIndex + 1,
          self: item,
        }));

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        return {};
      }
    },
    [client, selectedTerm, selectedCampus, selectedModality, selectedProgram],
  );

  const loadOptions = async (newSearchTerm: string) => {
    const newSearchPage = searchTerm === newSearchTerm ? page + 1 : 1;

    setSearchTerm(newSearchTerm);
    setPage(newSearchPage);

    const { pageInfo, options } = await requestItems(searchTerm, newSearchPage);
    dispatch({
      type: DataTypes.SetPopulations,
      payload: Object.values(options)?.map(option => option.self) ?? [],
    });

    return {
      options,
      hasMore: pageInfo?.hasNextPage,
      additional: { page },
    };
  };

  const resetPopulationOptions = () => {
    setSearchTerm("");
    setSelectedPopulation(null);
    dispatch({ type: DataTypes.SetPopulations, payload: [] });
  };

  const handleTermSelect = (term = "") => {
    resetPopulationOptions();
    setSelectedTerm(term);
  };

  const termKey = `${selectedTerm}${selectedCampus}${selectedModality}${selectedProgram}`;
  const isRequiredFieldsSelected = !!(
    selectedTerm &&
    (selectedCampus || selectedModality || selectedProgram)
  );
  const linkCampus = state?.data?.link?.course?.curriculum?.program?.campus;
  const isGlobalCampus = !!linkCampus?.isGlobal;

  useEffect(() => {
    setSelectedPopulation(null);
    setPage(0);
  }, [termKey]);

  useEffect(() => {
    onValidForm?.(!!selectedPopulation && !!packageName);

    return () => {
      onValidForm?.(false);
    };
  }, [selectedPopulation, packageName]);

  useListenEvent({
    eventKey: "on:submit",
    handler: (detail: Events["on:submit"]) => {
      if (detail?.target === "package-creation") {
        handlePackageCreation();
      }
    },
  });

  return (
    <section className={cx(css.creationForm, className)}>
      <div className={css.creationForm_content}>
        <TermSelect
          placeholder={t("create-package.forms.select-period.placeholder")}
          className={css.select}
          sendValues={({ term }) => handleTermSelect(term ?? "")}
          defaultTerm={state?.data?.link?.bundle?.term}
          isDisabled={!!state?.data?.link?.bundle?.term?.id}
        />

        <CampusSelect
          label={t("create-package.forms.select-campus.label")}
          placeholder={t("create-package.forms.select-campus.placeholder")}
          className={css.select}
          onCallback={(campus: IOption) => setSelectedCampus(campus?.value || "")}
          defaultCampus={!isGlobalCampus && linkCampus}
          isDisabled={!!linkCampus?.id && !isGlobalCampus}
        />
        <ModalitySelect
          label={t("create-package.forms.select-modality.label")}
          placeholder={t("create-package.forms.select-modality.placeholder")}
          className={css.select}
          shouldHasDepartment={false}
          onCallback={(modality: IOption) => setSelectedModality(modality?.value || "")}
        />
        <ProgramSelect
          className={css.select}
          label={t("create-package.forms.select-career.label")}
          placeholder={t("create-package.forms.select-career.placeholder")}
          campus={selectedCampus}
          modality={selectedModality}
          shouldHaveFilters={false}
          filterBy="modality"
          onCallback={(program: IOption) => setSelectedProgram(program?.value || "")}
        />

        <SelectPagination
          key={termKey}
          className={css.select}
          disabled={!isRequiredFieldsSelected}
          value={selectedPopulation}
          formatOptionLabel={(option: any) => selectorLabel(option?.self as Population)}
          debounceTimeout={500}
          label={t("create-package.forms.select-population.label")}
          placeholder={t("create-package.forms.select-population.placeholder")}
          onChange={setSelectedPopulation}
          loadOptions={loadOptions}
          onMenuClose={() => {
            if (searchTerm === "") return;

            setSearchTerm("");
            setPage(0);
          }}
        />
        <TextField
          className={css.textField}
          disabled={true}
          label={t("create-package.forms.input-numerator.label")}
          placeholder={t("create-package.forms.input-numerator.placeholder")}
          type={"number"}
          value={Boolean(selectedPopulation) ? selectedPopulation?.index : ""}
        />
        <TextField
          className={css.textField}
          disabled={!selectedPopulation || !selectedPopulation?.index}
          label={t("create-package.forms.input-package.label")}
          placeholder={t("create-package.forms.input-package.placeholder")}
          type="text"
          value={packageName}
          onChange={e => setPackageName(e.target.value)}
        />
      </div>
      {showActionButtons && (
        <div className={css.creationForm_actionsButtons}>
          <Button
            className={cx(css.selector__button)}
            disabled={isLoading}
            variant="ghost"
            onClick={onCancel}
          >
            {t("create-package.btn-cancel")}
          </Button>
          <Button
            disabled={!(selectedPopulation && packageName) || isLoading}
            className={cx(css.selector__button)}
            onClick={handlePackageCreation}
          >
            {t("create-package.btn-create")}
          </Button>
        </div>
      )}
    </section>
  );
};

export default memo(CreationForm);
